/* eslint-disable import-x/no-namespace */
import type React from 'react';

import { FolderIcon } from '@vlabs/icons';
import { SideMenu } from '@vlabs/uikit';
import { Button } from '@vlabs/uikit/components/controls/button';
import { DEFAULT_PAGE_SIZE } from '@vlabs/uikit/constants';

import { useEventApi } from '@vlabs/monokit/entities/events/api';
import { filterIsApplied, FilterMenu } from '@vlabs/monokit/shared/ui/filter-menu';

import { useEventListFiltersTranslation } from './i18n';
import { hasLocationFilters } from './popovers/location/geolocation';
import * as sideMenuFilters from './side-menu';
import { useEventListFilters } from './useEventListFilters';

interface EventListFilterMenuProps {
  includedAttributes?: string[];
}

function EventListFilterMenu({
  includedAttributes = [],
}: EventListFilterMenuProps): React.JSX.Element {
  const { t } = useEventListFiltersTranslation();
  const { filters, setFilters } = useEventListFilters();
  const api = useEventApi();
  const { externalIdField } = api.capabilities;
  const hasTopSimilarCardIds = api.capabilities.topSimilarCardIds;

  const EventIdItem = externalIdField === 'card_ids'
    ? sideMenuFilters.CardIdFilterWithLeftPopover
    : sideMenuFilters.EventIdFilterWithLeftPopover;

  const $filters = [
    <SideMenu.MenuItem
      data-testid="eventList.generalFilters"
      key="general"
      leftIcon={<FolderIcon />}
      value={t('group_general')}
    >
      <sideMenuFilters.DateFilterWithLeftPopover
        badge={filterIsApplied(!!(filters.create_time__lt ?? filters.create_time__gte))}
        key="create_time"
        testId="create_time-filter"
        value={t('create_time')}
      />
      <sideMenuFilters.SourceFilterWithLeftPopover
        badge={filterIsApplied(!!(filters.sources))}
        key="sources"
        testId="sources-filter"
        value={t('sources')}
      />
      <sideMenuFilters.HandlersFilterWithLeftPopover
        badge={filterIsApplied(!!(filters.handler_ids))}
        key="handler_ids"
        testId="handler_ids-filter"
        value={t('handler_ids')}
      />
      <sideMenuFilters.ListsFilterWithLeftPopover
        badge={filterIsApplied(!!(filters.top_matching_candidates_label))}
        key="top_matching_candidates_label"
        testId="top_matching_candidates_label-filter"
        value={t('lists')}
      />
      <sideMenuFilters.PermissionTagsFilterWithLeftPopover
        badge={filterIsApplied(!!(filters.tags?.length))}
        key="tags"
        testId="tags-filter"
        value={t('tags')}
      />
      <EventIdItem
        badge={filterIsApplied(!!(filters.event_ids?.length ?? filters[externalIdField]?.length))}
        key="event_id"
        testId="event_id-filter"
        value={t('event_ids')}
      />
    </SideMenu.MenuItem>,
    <SideMenu.MenuItem
      data-testid="eventList.comparisonFilters"
      key="Comparing attributes"
      leftIcon={<FolderIcon />}
      value={t('group_comparing_attributes')}
    >
      <sideMenuFilters.ComparingAttributeFilterWithLeftPopover
        badge={filterIsApplied(!!(filters.top_matching_candidates_label))}
        testId="comparing_attribute-filter"
        value={t('comparing_attribute')}
      />
      <sideMenuFilters.SimilarityFilterWithLeftPopover
        badge={filterIsApplied(!!(filters.top_similar_object_similarity__gte
          ?? filters.top_similar_object_similarity__lt))}
        testId="similarity-filter"
        value={t('top_similar_object_similarity')}
      />
      <sideMenuFilters.TopSimilarObjectsFilterWithLeftPopover
        badge={filterIsApplied(!!(filters.top_similar_object_ids?.length))}
        testId="top_similar_object_ids-filter"
        value={t('top_similar_object_ids')}
      />
      {!!hasTopSimilarCardIds && (
        <sideMenuFilters.TopSimilarCardIdsFilterWithLeftPopover
          badge={filterIsApplied(!!(filters.top_similar_card_ids?.length))}
          testId="top_similar_card_ids-filter"
          value={t('top_similar_card_ids')}
        />
      )}
    </SideMenu.MenuItem>,
    <SideMenu.MenuItem
      data-testid="eventList.faceAttributesFilters"
      key="Face attributes"
      leftIcon={<FolderIcon />}
      value={t('group_face_attributes')}
    >
      <sideMenuFilters.GenderFilterWithLeftPopover
        badge={filterIsApplied(filters.gender !== undefined)}
        testId="gender-filter"
        value={t('gender')}
      />
      <sideMenuFilters.AgeFilterWithLeftPopover
        badge={filterIsApplied(filters.age__gte !== undefined || filters.age__lt !== undefined)}
        testId="age-filter"
        value={t('age')}
      />
      <sideMenuFilters.EmotionsFilterWithLeftPopover
        badge={filterIsApplied(!!(filters.emotions?.length))}
        testId="emotions-filter"
        value={t('emotions')}
      />
      <sideMenuFilters.MaskFilterWithLeftPopover
        badge={filterIsApplied(!!(filters.masks?.length))}
        testId="masks-filter"
        value={t('masks')}
      />
      <sideMenuFilters.LivenessFilterWithLeftPopover
        badge={filterIsApplied(!!(filters.liveness?.length))}
        testId="liveness-filter"
        value={t('liveness')}
      />
      <sideMenuFilters.DeepfakeFilterWithLeftPopover
        badge={filterIsApplied(!!(filters.deepfake?.length))}
        testId="deepfake-filter"
        value={t('deepfake')}
      />
      <sideMenuFilters.FaceIdFilterWithLeftPopover
        badge={filterIsApplied(!!(filters.face_ids?.length))}
        testId="face_ids-filter"
        value={t('face_ids')}
      />
    </SideMenu.MenuItem>,
    <SideMenu.MenuItem
      data-testid="eventList.bodyAttributesFilters"
      key="Body attributes"
      leftIcon={<FolderIcon />}
      value={t('group_body_attributes')}
    >
      <sideMenuFilters.ApparentGenderFilterWithLeftPopover
        badge={filterIsApplied(!!(filters.apparent_gender?.length))}
        testId="apparent_gender-filter"
        value={t('apparent_gender')}
      />
      <sideMenuFilters.ApparentAgeFilterWithLeftPopover
        badge={filterIsApplied(
          filters.apparent_age__gte !== undefined || filters.apparent_age__lt !== undefined,
        )}
        testId="apparent_age-filter"
        value={t('apparent_age')}
      />
      <sideMenuFilters.UpperAttributesFilterWithLeftPopover
        badge={filterIsApplied(
          !!(filters.upper_clothing_colors?.length ?? filters.sleeve_lengths?.length),
        )}
        testId="upper_attributes-filter"
        value={t('upper_clothing')}
      />
      <sideMenuFilters.LowerAttributesFilterWithLeftPopover
        badge={filterIsApplied(
          !!(filters.lower_garment_colors?.length ?? filters.lower_garment_types?.length),
        )}
        testId="lower_attributes-filter"
        value={t('lower_garment_colors')}
      />
      <sideMenuFilters.ShoesColorFilterWithLeftPopover
        badge={filterIsApplied(!!(filters.shoes_apparent_colors?.length))}
        testId="shoes_apparent_colors-filter"
        value={t('shoes_apparent_colors')}
      />
      <sideMenuFilters.HeadwearFilterWithLeftPopover
        badge={filterIsApplied(
          !!(filters.headwear_apparent_colors?.length ?? filters.headwear_states?.length),
        )}
        testId="headwear-filter"
        value={t('headwear')}
      />
      <sideMenuFilters.BackpackFilterWithLeftPopover
        badge={filterIsApplied(!!(filters.backpack_states?.length))}
        testId="backpack_states-filter"
        value={t('backpack_states')}
      />
    </SideMenu.MenuItem>,
    <sideMenuFilters.LocationFilterWithLeftPopover
      badge={filterIsApplied(hasLocationFilters(filters))}
      key="Location"
      leftIcon={<FolderIcon />}
      popoverProps={{ includedAttributes }}
      testId="location-filter"
      value={t('location')}
    />,
    <SideMenu.MenuItem
      data-testid="eventList.otherFilters"
      key="Others"
      leftIcon={<FolderIcon />}
      value={t('group_others')}
    >
      <sideMenuFilters.TrackIdFilterWithLeftPopover
        badge={filterIsApplied(!!(filters.track_ids?.length))}
        testId="track_ids-filter"
        value={t('track_ids')}
      />
      <sideMenuFilters.MetaFilterWithLeftPopover
        badge={filterIsApplied(Object.keys(filters).some((key) => key.startsWith('meta.')))}
        testId="meta-filter"
        value={t('meta')}
      />
    </SideMenu.MenuItem>,
  ];

  return (
    <FilterMenu
      filters={$filters}
      footer={(
        <Button
          data-testid="button.reset-event-list-filters"
          fullWidth
          kind="negative"
          onClick={() => setFilters({
            page: 1,
            page_size: DEFAULT_PAGE_SIZE,
          })}
          type="button"
        >
          {t('reset')}
        </Button>
      )}
    />
  );
}

export { EventListFilterMenu };
